import React from "react";
import { useTranslation } from "react-i18next";
import PropTypes from "prop-types";
import getStyled from "../../hooks/getStyled";
import Rule from "./rule";
import SFTypography from "../typography";
import { SFStack } from "../grid";
import SFCard, { SFCardHeader, SFCardBody } from "../card";

const { styled } = getStyled();

const SFStackStyled = styled(SFStack)(({ theme }) => ({
  paddingTop: theme.spacing(2),
}));

const SFCardStyled = styled(SFCard)(({ theme }) => ({
  overflow: "unset",
  borderRadius: 4,
  marginTop: theme.spacing(1),
  marginBottom: theme.spacing(3),
}));

const SFCardHeaderStyled = styled(SFCardHeader)(({ theme }) => ({
  backgroundColor: theme.palette.grey100.main,
  padding: theme.spacing(1.5),
}));

const SFCardBodyStyled = styled(SFCardBody)(({ theme }) => ({
  // paddingTop: theme.spacing(2.5),
}));

const SFTypographyCombinatorStyled = styled(SFTypography)(({ theme }) => ({
  padding: theme.spacing(3, 0),
}));

const SFTypographyGroupCombinatorStyled = styled(SFTypography)(({ theme }) => ({
  padding: theme.spacing(2, 0),
}));

const SFCardGroupStyled = styled(SFCard)(({ theme }) => ({
  border: `2px dashed ${theme.palette.grey200.main}`,
  borderRadius: "4px",
  padding: theme.spacing(1),
}));

const SFPreviewQuery = ({ savedQuery, columnDefs }) => {
  const { t } = useTranslation();
  let savedQueryPreview =
    savedQuery?.query?.length > 0 && JSON.parse(savedQuery.query);

  const drawQuery = (rule, index = 0, query = {}) => {
    if (Object.prototype.hasOwnProperty.call(rule, "rules")) {
      return <Group group={rule} key={index} index={index} />;
    } else if (typeof rule === "string" || rule instanceof String) {
      const queryUcase = rule.toUpperCase();
      //check if next rule exists and is a group
      //added this check because the spacing between operators and groups/rules is different
      let isNextRuleGroup = Object.prototype.hasOwnProperty.call(
        query[index + 1] ?? {},
        "rules"
      );
      if (rule.toUpperCase() === "OR" || rule.toUpperCase() === "AND") {
        if (isNextRuleGroup) {
          return (
            <SFTypographyGroupCombinatorStyled
              key={index}
              variant="body2bold"
              color="grey350"
            >
              {queryUcase}
            </SFTypographyGroupCombinatorStyled>
          );
        }
        return (
          <SFTypographyCombinatorStyled
            key={index}
            variant="body2bold"
            color="grey350"
          >
            {queryUcase}
          </SFTypographyCombinatorStyled>
        );
      }
    } else if (Object.prototype.hasOwnProperty.call(rule, "operator")) {
      const colDef = columnDefs.find((x) => x.field === rule.field);
      return (
        <Rule
          rule={rule}
          key={index}
          index={index}
          fieldName={colDef?.headerName}
          valueFormatter={colDef?.valueFormatter}
        />
      );
    } else return null;
  };

  const Group = ({ group, index }) => {
    return (
      <React.Fragment key={index}>
        <SFStack direction="row" spacing={0.5}>
          {group.not ? (
            <SFTypography variant="body2bold" color="grey350">
              {t("previewQuery.combinator.not")}
            </SFTypography>
          ) : (
            <SFTypography variant="body2bold" color="grey350">
              {t("previewQuery.combinator.is")}
            </SFTypography>
          )}
        </SFStack>
        <SFCardGroupStyled>
          <SFStack direction="column">
            {group.rules.map((q, i) => drawQuery(q, i, group.rules))}
          </SFStack>
        </SFCardGroupStyled>
      </React.Fragment>
    );
  };

  return (
    <>
      <SFStackStyled direction="column" spacing={2}>
        <SFTypography variant="h4">{t("previewQuery.header")}</SFTypography>
        {savedQuery?.query?.length > 0 ? (
          <SFCardStyled>
            <SFCardHeaderStyled
              title={
                <SFTypography variant="tableHeader">
                  {savedQuery.name}
                </SFTypography>
              }
            />
            <SFCardBodyStyled>{drawQuery(savedQueryPreview)}</SFCardBodyStyled>
          </SFCardStyled>
        ) : (
          <SFTypography variant="body1">
            {t("previewQuery.selectQueryLabel")}
          </SFTypography>
        )}
      </SFStackStyled>
    </>
  );
};

SFPreviewQuery.propTypes = {
  savedQuery: PropTypes.object,
  columnDefs: PropTypes.array.isRequired,
};

export default SFPreviewQuery;
